import { phoneNumberHelper } from '@upsales/common';

export const COPYABLE_ADDRESS_FIELDS = ['address', 'zipcode', 'city', 'municipality', 'state', 'country'] as const;

export type CopyableAddressField = (typeof COPYABLE_ADDRESS_FIELDS)[number];
export type CopyableAddress = Partial<Record<CopyableAddressField, string | null>>;

const normalizeField = (address: CopyableAddress | null | undefined, field: CopyableAddressField) => {
	const value = (address?.[field] ?? '').toString().trim();
	return field === 'country' ? value.toUpperCase() : value;
};

export const hasAddressValue = (address: CopyableAddress | null | undefined) =>
	COPYABLE_ADDRESS_FIELDS.some(field => normalizeField(address, field) !== '');

export const isSameAddress = (a: CopyableAddress | null | undefined, b: CopyableAddress | null | undefined) =>
	COPYABLE_ADDRESS_FIELDS.every(field => normalizeField(a, field) === normalizeField(b, field));

export const pickCopyableAddressFields = (address: CopyableAddress) =>
	COPYABLE_ADDRESS_FIELDS.reduce<Record<CopyableAddressField, string | null>>(
		(res, field) => {
			res[field] = address[field] ?? null;
			return res;
		},
		{} as Record<CopyableAddressField, string | null>
	);

export const getAddressSummary = (address: CopyableAddress) => {
	const street = (address.address ?? '')
		.split(/\r?\n/)
		.map(line => line.trim())
		.filter(Boolean)
		.join(', ');
	const zipCity = [address.zipcode, address.city]
		.map(v => (v ?? '').trim())
		.filter(Boolean)
		.join(' ');
	const summary = [street, zipCity].filter(Boolean).join(', ');
	if (summary) {
		return summary;
	}
	return [address.municipality, address.state, address.country]
		.map(v => (v ?? '').trim())
		.filter(Boolean)
		.join(', ');
};

const POSTCODE_SEGMENT = /^(\d{3} ?\d{2})\s+(\S.*)$/;
const COUNTRY_SEGMENT = /^(sweden|sverige)$/i;

export const splitPastedAddress = (text: string): { address: string; zipcode: string; city: string } | null => {
	const segments = text
		.split(/\r\n|\r|\n/)
		.flatMap((line, lineIndex) => line.split(',').map(segment => ({ lineIndex, text: segment.trim() })))
		.filter(segment => segment.text);

	let postcodeIndex = -1;
	for (let i = segments.length - 1; i >= 0; i--) {
		if (POSTCODE_SEGMENT.test(segments[i].text)) {
			postcodeIndex = i;
			break;
		}
	}
	if (postcodeIndex <= 0) {
		return null;
	}

	const trailing = segments.slice(postcodeIndex + 1);
	if (!trailing.every(segment => COUNTRY_SEGMENT.test(segment.text))) {
		return null;
	}

	const [, zipcode, city] = segments[postcodeIndex].text.match(POSTCODE_SEGMENT)!;
	const address = segments
		.slice(0, postcodeIndex)
		.map((segment, i, street) =>
			i === 0 ? segment.text : `${street[i - 1].lineIndex === segment.lineIndex ? ', ' : '\n'}${segment.text}`
		)
		.join('');

	return { address, zipcode, city: city.trim() };
};

export const getPhoneCountryCode = (country: string | null | undefined) => {
	const countryCode = (country ?? '').toLowerCase();
	return countryCode && phoneNumberHelper.getCountryCodeForRegion(countryCode.toUpperCase()) ? countryCode : null;
};

export const isEmptyOrFullySelected = (el: HTMLInputElement | HTMLTextAreaElement) =>
	el.value === '' || (el.selectionStart === 0 && el.selectionEnd === el.value.length);

export const formatPostcodeForDisplay = <T extends string | null | undefined>(
	zipcode: T,
	country: string | null | undefined
): T | string => {
	if (typeof zipcode === 'string' && (country ?? '').toUpperCase() === 'SE' && /^\d{5}$/.test(zipcode)) {
		return `${zipcode.slice(0, 3)} ${zipcode.slice(3)}`;
	}
	return zipcode;
};
